if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.coreInitialized = false;
      this.coreObserver = null;
    }

    connectedCallback() {
      this.initializeCoreWhenReady();
    }

    disconnectedCallback() {
      if (this.coreObserver) {
        this.coreObserver.disconnect();
        this.coreObserver = null;
      }
    }

    initializeCoreWhenReady() {
      if (this.coreInitialized) return;

      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        this.initializeCore(overlay);
        return;
      }

      if (this.coreObserver) return;
      this.coreObserver = new MutationObserver(() => {
        const nextOverlay = this.querySelector('#CartDrawer-Overlay');
        if (!nextOverlay) return;
        this.coreObserver.disconnect();
        this.coreObserver = null;
        this.initializeCore(nextOverlay);
      });
      this.coreObserver.observe(this, { childList: true, subtree: true });
    }

    initializeCore(overlay) {
      if (this.coreInitialized) return;
      this.coreInitialized = true;
      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      overlay.addEventListener('click', this.close.bind(this));
      this.setHeaderCartIconAccessibility();
      this.dataset.ready = 'true';
      document.documentElement.classList.add('cart-drawer-ready');
      this.dispatchEvent(new CustomEvent('cart-drawer:ready', { bubbles: true }));
      this.scheduleRecommendSwiper();
    }

    scheduleRecommendSwiper() {
      if (this.recommendInitScheduled) return;
      this.recommendInitScheduled = true;

      const run = () => {
        this.recommendInitScheduled = false;
        this.initRecommendSwiper();
      };

      if ('requestIdleCallback' in window) {
        requestIdleCallback(run, { timeout: 1500 });
      } else {
        setTimeout(run, 0);
      }
    }

    initRecommendSwiper() {
      if (this.cartrecommendswiper && typeof this.cartrecommendswiper.destroy === 'function') {
        this.cartrecommendswiper.destroy(true, true);
        this.cartrecommendswiper = null;
      }

      const recommend = this.querySelector('.cart-recommend-swiper');
      if (!recommend || typeof window.Swiper !== 'function') return;

      this.cartrecommendswiper = new window.Swiper(recommend, {
        direction: 'horizontal',
        loop: true,
        autoplay: false,
        slidesPerView: 2.6,
        spaceBetween: 10,
        navigation: {
          nextEl: '.bolg-swiper-button-next',
        },
        breakpoints: {
          765: {
            slidesPerView: 2.6,
            spaceBetween: 10,
          },
          360: {
            spaceBetween: 10,
            slidesPerView: 2,
          },
        },
      });
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink || cartLink.dataset.cartDrawerBound === 'true') return;

      cartLink.dataset.cartDrawerBound = 'true';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

      requestAnimationFrame(() => {
        this.classList.add('animate', 'active');
      });

      this.addEventListener(
        'transitionend',
        () => {
          const trapFocusFn = window.trapFocus;
          if (typeof trapFocusFn !== 'function') return;
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          if (containerToTrapFocusOn && focusElement) trapFocusFn(containerToTrapFocusOn, focusElement);
        },
        { once: true }
      );

      document.body.classList.add('overflow-hidden');
      this.scheduleRecommendSwiper();
    }

    close() {
      this.classList.remove('active');
      const removeTrapFocusFn = window.removeTrapFocus;
      if (typeof removeTrapFocusFn === 'function') removeTrapFocusFn(this.activeElement);
      document.body.classList.remove('overflow-hidden');
    }

    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute('role', 'button');
      cartDrawerNote.setAttribute('aria-expanded', 'false');

      if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
        cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
      }

      cartDrawerNote.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
      });

      const onKeyUpEscapeFn = window.onKeyUpEscape;
      if (typeof onKeyUpEscapeFn === 'function') {
        cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscapeFn);
      }
    }

    renderContents(parsedState) {
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner && drawerInner.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');
      this.productId = parsedState.id;

      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement || !parsedState.sections || !parsedState.sections[section.id]) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });

      requestAnimationFrame(() => {
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', this.close.bind(this));
        this.setHeaderCartIconAccessibility();
        this.open();
        this.scheduleRecommendSwiper();
      });
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      return parsed ? parsed.innerHTML : '';
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          selector: '#CartDrawer',
        },
        {
          id: 'cart-icon-bubble',
        },
      ];
    }

    getSectionDOM(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
